import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input, Label, Textarea } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Send, Copy, Trash2, CheckCircle2, AlertTriangle, Clock, ExternalLink } from "lucide-react";
import { formatNum, formatSgd } from "../lib/utils";
import { toast } from "sonner";

const STATUS_META = {
  sent: { tone: "warning", icon: Clock, label: "Awaiting" },
  responded: { tone: "lime", icon: CheckCircle2, label: "Responded" },
  confirmed: { tone: "success", icon: CheckCircle2, label: "Confirmed" },
  overdue: { tone: "danger", icon: AlertTriangle, label: "Overdue" },
};

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reqOpen, setReqOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(null);
  const [reqForm, setReqForm] = useState({ supplier_id: "", period: "2024", due_days: 30 });
  const [confirmAttribution, setConfirmAttribution] = useState({ co2e: "", notes: "" });

  const load = async () => {
    const [s, r] = await Promise.all([
      api.get("/suppliers"),
      api.get("/suppliers/requests/all"),
    ]);
    setSuppliers(s.data.suppliers);
    setRequests(r.data.requests);
    api.get("/valueprint/supplier-categories").then((c) => setCategories(c.data.categories)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Mark overdue client-side
  const enrichedReqs = requests.map(r => ({
    ...r,
    status: r.status === "sent" && r.due_date && new Date(r.due_date) < new Date() ? "overdue" : r.status,
  }));

  const createReq = async () => {
    try {
      const { data } = await api.post("/suppliers/requests", { ...reqForm, due_days: parseInt(reqForm.due_days) });
      const shareUrl = `${window.location.origin}${data.share_link}`;
      navigator.clipboard?.writeText(shareUrl);
      toast.success("Request sent (SIMULATED). Share link copied to clipboard.");
      setReqOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const removeReq = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    await api.delete(`/suppliers/requests/${id}`);
    load();
  };

  const confirmResponse = async () => {
    try {
      await api.post("/suppliers/requests/confirm", {
        request_id: confirmOpen.id,
        attributed_co2e_tonnes: parseFloat(confirmAttribution.co2e),
        notes: confirmAttribution.notes || null,
      });
      toast.success("Confirmed — Scope 3 Cat 1 data point created");
      setConfirmOpen(null);
      setConfirmAttribution({ co2e: "", notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/supplier/respond/${token}`;
    navigator.clipboard?.writeText(url);
    toast.success("Share link copied");
  };

  const totalSpend = suppliers.reduce((acc, s) => acc + (s.spend_sgd_annual || 0), 0);
  const respondedPct = requests.length > 0 ? Math.round(requests.filter(r => ["responded", "confirmed"].includes(r.status)).length / requests.length * 100) : 0;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">Module · Scope 3 Cat. 1 · Engagement Portal</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Supplier Engagement</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            Collect primary-data emissions from suppliers via token-gated public response pages. Attribute confirmed data to Scope 3 Category 1.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div className="card-flat p-3">
            <div className="label-eyebrow mb-1">Response rate</div>
            <div className="font-display text-2xl font-bold">{respondedPct}%</div>
          </div>
          <div className="card-flat p-3">
            <div className="label-eyebrow mb-1">Spend covered</div>
            <div className="font-display text-2xl font-bold">{formatSgd(totalSpend)}</div>
          </div>
        </div>
      </div>

      {!!categories.length && (
        <div className="card-flat p-4" data-testid="vp-supplier-categories">
          <div className="label-eyebrow mb-2">Supplier categories — from your Lestari ValuePrint template</div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <Badge key={c.id} variant="outline" title={`${c.description || ""} (${c.template_source})`} data-testid={`vp-supplier-cat-${c.id}`}>
                {c.name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <Tabs defaultValue="requests">
        <TabsList>
          <TabsTrigger value="requests" data-testid="tab-requests">Data Requests ({enrichedReqs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="requests">
          <div className="flex justify-end mb-3">
            <Dialog open={reqOpen} onOpenChange={setReqOpen}>
              <DialogTrigger asChild>
                <Button variant="primary" size="sm" data-testid="new-request-btn"><Plus className="h-4 w-4" /> New request</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Send data request</DialogTitle><DialogDescription>SIMULATED email — share link will be copied to clipboard.</DialogDescription></DialogHeader>
                <div>
                  <Label>Supplier</Label>
                  <Select value={reqForm.supplier_id} onValueChange={(v) => setReqForm({...reqForm, supplier_id: v})}>
                    <SelectTrigger data-testid="req-supplier-select"><SelectValue placeholder="Choose supplier" /></SelectTrigger>
                    <SelectContent>
                      {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Period (year)</Label><Input value={reqForm.period} onChange={(e) => setReqForm({...reqForm, period: e.target.value})} data-testid="req-period-input" /></div>
                  <div><Label>Due in (days)</Label><Input type="number" value={reqForm.due_days} onChange={(e) => setReqForm({...reqForm, due_days: e.target.value})} data-testid="req-due-input" /></div>
                </div>
                <Button variant="primary" onClick={createReq} disabled={!reqForm.supplier_id} data-testid="req-submit-btn"><Send className="h-4 w-4" /> Send</Button>
              </DialogContent>
            </Dialog>
          </div>

          <div className="card-flat overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Response</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enrichedReqs.map((r) => {
                  const s = STATUS_META[r.status] || STATUS_META.sent;
                  const SI = s.icon;
                  return (
                    <TableRow key={r.id} data-testid={`request-${r.id}`}>
                      <TableCell className="font-medium">{r.supplier_name || "—"}<div className="text-xs text-muted-foreground">{r.supplier_email}</div></TableCell>
                      <TableCell className="font-mono text-xs">{r.period}</TableCell>
                      <TableCell className="font-mono text-xs">{r.due_date ? new Date(r.due_date).toLocaleDateString() : "—"}</TableCell>
                      <TableCell><Badge variant={s.tone}><SI className="h-3 w-3 mr-1" /> {s.label}</Badge></TableCell>
                      <TableCell className="text-xs">
                        {r.responses ? (
                          <details>
                            <summary className="cursor-pointer text-accent">View ({Object.keys(r.responses).length})</summary>
                            <div className="mt-2 font-mono text-[10px] space-y-0.5">
                              {Object.entries(r.responses).map(([k, v]) => <div key={k}>{k}: <span className="text-foreground">{v}</span></div>)}
                            </div>
                          </details>
                        ) : "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => copyLink(r.token)} title="Copy link" data-testid="copy-link-btn"><Copy className="h-3.5 w-3.5" /></Button>
                          {r.status === "responded" && (
                            <Button variant="ghost" size="sm" onClick={() => setConfirmOpen(r)} className="text-xs" data-testid="confirm-response-btn">Confirm</Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => removeReq(r.id)} data-testid="delete-request-btn"><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {enrichedReqs.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No requests yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Confirm-response dialog */}
      <Dialog open={!!confirmOpen} onOpenChange={(o) => !o && setConfirmOpen(null)}>
        <DialogContent>
          {confirmOpen && (
            <>
              <DialogHeader>
                <DialogTitle>Confirm supplier response</DialogTitle>
                <DialogDescription>{confirmOpen.supplier_name} · Period {confirmOpen.period}</DialogDescription>
              </DialogHeader>
              <div className="card-flat p-3 bg-surface">
                <div className="label-eyebrow mb-2">Supplier-reported figures</div>
                <div className="space-y-1 text-sm font-mono">
                  {confirmOpen.responses && Object.entries(confirmOpen.responses).map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-muted-foreground">{k}</span>
                      <span>{formatNum(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label>Attribute to Scope 3 Cat 1 (tCO2e)</Label>
                <Input type="number" step="any" value={confirmAttribution.co2e} onChange={(e) => setConfirmAttribution({...confirmAttribution, co2e: e.target.value})} placeholder="e.g. 92.0" data-testid="confirm-attribution-input" />
                <div className="text-xs text-muted-foreground mt-1">Typically a share-of-revenue or spend-based allocation of the supplier's reported Scope 1+2.</div>
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea value={confirmAttribution.notes} onChange={(e) => setConfirmAttribution({...confirmAttribution, notes: e.target.value})} rows={2} />
              </div>
              <Button variant="primary" onClick={confirmResponse} disabled={!confirmAttribution.co2e} data-testid="confirm-attribute-btn">Confirm & create S3 Cat 1 data point</Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
